import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Steps } from 'primereact/steps';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import CompanyInfoStep from './CompanyInfoStep';
import AdminDetailsStep from './AdminDetailsStep';
import ReviewSubmitStep from './ReviewSubmitStep';
import companyService from '../../services/companyService';

const CompanyRegistrationWizard = () => {
    const navigate = useNavigate();
    const toast = useRef(null);

    // Wizard state
    const [activeStep, setActiveStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    // Form data
    const [formData, setFormData] = useState({
        // Step 1: Company Info
        company_name: '',
        gst_number: '',
        address: '',

        // Step 2: Establishment Details (all fields from EstablishmentStep)
        type_of_establishment: '',
        // Dynamic fields based on establishment type will be added here
        has_gst_certificate: false,
        gst_certificate_number: '',
        // Bank details
        bank_account_name: '',
        account_number: '',
        account_type: '',
        ifsc_code: '',
        bank_name: '',

        // Step 3: Owner/Admin Details
        first_name: '',
        last_name: '',
        email: '', // This becomes company email
        mobile_number: '', // This becomes company phone
        password: '',
        password_confirmation: '',

        // OTP verification
        verified_otp: '', // OTP token from verification
    });

    // Verification states
    const [emailVerified, setEmailVerified] = useState(false);
    const [verifiedOtp, setVerifiedOtp] = useState('');
    const [companyNameAvailable, setCompanyNameAvailable] = useState(null);

    // Validation errors
    const [errors, setErrors] = useState({});

    // Wizard steps configuration
    const wizardSteps = [
        { label: 'Company & Establishment' },
        { label: 'Owner Details' },
        { label: 'Review' }
    ];

    // Update form data
    const updateFormData = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    // Handle OTP verification success
    const handleEmailVerified = (otp) => {
        setVerifiedOtp(otp);
        setEmailVerified(true);
        setFormData(prev => ({
            ...prev,
            verified_otp: otp
        }));

        toast.current.show({
            severity: 'success',
            summary: 'Email Verified',
            detail: 'Your email has been verified successfully!',
            life: 3000
        });
    };

    // Handle company name availability
    const handleCompanyNameCheck = (available) => {
        setCompanyNameAvailable(available);
    };

    // Validate current step
    const validateStep = (stepIndex) => {
        const newErrors = {};

        if (stepIndex === 0) {
            // Company & Establishment validation
            if (!formData.company_name || !formData.company_name.trim()) {
                newErrors.company_name = 'Company name is required';
            }
            if (companyNameAvailable === false) {
                newErrors.company_name = 'This company name is already registered';
            }
            if (!formData.address || !formData.address.trim()) {
                newErrors.address = 'Address is required';
            }
            if (!formData.type_of_establishment) {
                newErrors.type_of_establishment = 'Type of establishment is required';
            }
        }

        if (stepIndex === 1) {
            // Owner Details validation
            if (!formData.first_name || !formData.first_name.trim()) {
                newErrors.first_name = 'First name is required';
            }
            if (!formData.last_name || !formData.last_name.trim()) {
                newErrors.last_name = 'Last name is required';
            }
            if (!formData.email) {
                newErrors.email = 'Email is required';
            }
            if (!emailVerified) {
                newErrors.email = 'Please verify your email';
            }
            if (!formData.mobile_number) {
                newErrors.mobile_number = 'Mobile number is required';
            } else if (formData.mobile_number.length !== 10) {
                newErrors.mobile_number = 'Mobile number must be 10 digits';
            }
            if (!formData.password) {
                newErrors.password = 'Password is required';
            } else if (formData.password.length < 8) {
                newErrors.password = 'Password must be at least 8 characters';
            }
            if (formData.password !== formData.password_confirmation) {
                newErrors.password_confirmation = 'Passwords do not match';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Navigate to next step
    const handleNext = () => {
        if (validateStep(activeStep)) {
            setActiveStep(prev => prev + 1);
        } else {
            toast.current.show({
                severity: 'warn',
                summary: 'Validation Error',
                detail: 'Please fill in all required fields correctly',
                life: 3000
            });
        }
    };

    // Navigate to previous step
    const handlePrevious = () => {
        setActiveStep(prev => prev - 1);
    };

    // Handle final submission
    const handleSubmit = async () => {
        if (!validateStep(activeStep - 1)) {
            toast.current.show({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please check all information',
                life: 3000
            });
            return;
        }

        setSubmitting(true);

        try {
            // Prepare registration data with OTP
            const registrationData = {
                ...formData,
                otp: verifiedOtp // Send the verified OTP token
            };

            const response = await companyService.register(registrationData);

            if (response.success) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Company registered successfully!',
                    life: 3000
                });

                // Store token, user and roles (same as normal login)
                localStorage.setItem('authToken', response.data.token);
                localStorage.setItem('user', JSON.stringify(response.data.user));
                localStorage.setItem('isAuthenticated', 'true');

                // Store user roles for permission checks (fixes User Management access)
                if (response.data.roles) {
                    localStorage.setItem('userRoles', JSON.stringify(response.data.roles));
                }

                // Redirect to dashboard after 2 seconds
                setTimeout(() => {
                    navigate('/app/dashboard');
                }, 2000);
            }
        } catch (error) {
            // companyService throws error.response?.data directly, so check both formats
            const message = error.message || error.response?.data?.message || 'Registration failed. Please try again.';
            const validationErrors = error.errors || error.response?.data?.errors || {};

            // Laravel returns errors as arrays, extract first error for each field
            const formattedErrors = {};
            Object.keys(validationErrors).forEach(key => {
                formattedErrors[key] = Array.isArray(validationErrors[key])
                    ? validationErrors[key][0]
                    : validationErrors[key];
            });

            setErrors(formattedErrors);

            // Check if it's an OTP-related error and reset email verification
            if (message.toLowerCase().includes('otp') ||
                message.toLowerCase().includes('expired') ||
                message.toLowerCase().includes('verify your email')) {
                setEmailVerified(false);
                setVerifiedOtp('');
            }

            toast.current.show({
                severity: 'error',
                summary: 'Registration Failed',
                detail: message,
                life: 5000
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Check if step is valid
    const isStepValid = (stepIndex) => {
        if (stepIndex === 0) {
            // Step 1: Company & Establishment
            return formData.company_name &&
                   formData.address &&
                   formData.type_of_establishment &&
                   companyNameAvailable !== false;
        }

        if (stepIndex === 1) {
            // Step 2: Owner/Admin Details
            return emailVerified &&
                   formData.first_name &&
                   formData.last_name &&
                   formData.email &&
                   formData.mobile_number &&
                   formData.password &&
                   formData.password === formData.password_confirmation;
        }

        return true;
    };

    return (
        <div className="flex flex-column align-items-center justify-content-center">
            <Toast ref={toast} />

            <div className="w-full surface-card py-4 px-3 sm:px-6" style={{ borderRadius: '12px', maxWidth: '600px' }}>
                {/* Logo Section */}
                <div className="text-center mb-2">
                    <img src={`${process.env.PUBLIC_URL}/layout/images/interestng_logo_updated.jpeg`} alt="INTERESTNG" style={{ height: '48px', width: 'auto', mixBlendMode: 'multiply', filter: 'contrast(1.1) brightness(1.05)' }} />
                </div>

                {/* Header */}
                <div className="text-center mb-3">
                    <div className="text-900 text-xl font-medium mb-1">Company Registration</div>
                    <span className="text-600 font-medium text-sm">Create your company account and manage your team</span>
                </div>

                {/* Wizard Progress */}
                <Steps
                    model={wizardSteps}
                    activeIndex={activeStep}
                    readOnly={true}
                    className="mb-4"
                />

                {/* Step Content */}
                <div className="wizard-content">
                    {activeStep === 0 && (
                        <CompanyInfoStep
                            formData={formData}
                            updateFormData={updateFormData}
                            errors={errors}
                            companyNameAvailable={companyNameAvailable}
                            onCompanyNameCheck={handleCompanyNameCheck}
                            toast={toast}
                        />
                    )}

                    {activeStep === 1 && (
                        <AdminDetailsStep
                            formData={formData}
                            updateFormData={updateFormData}
                            errors={errors}
                            emailVerified={emailVerified}
                            onEmailVerified={handleEmailVerified}
                            toast={toast}
                        />
                    )}

                    {activeStep === 2 && (
                        <ReviewSubmitStep
                            formData={formData}
                            emailVerified={emailVerified}
                            companyNameAvailable={companyNameAvailable}
                        />
                    )}
                </div>

                {/* Navigation Buttons */}
                <div className="flex justify-content-between mt-3 pt-3 border-top-1 border-200">
                    {activeStep > 0 ? (
                        <Button
                            label="Back"
                            icon="pi pi-angle-left"
                            onClick={handlePrevious}
                            disabled={submitting}
                            className="p-button-text"
                        />
                    ) : (
                        <div></div>
                    )}

                    {activeStep < 2 ? (
                        <Button
                            label="Next"
                            icon="pi pi-angle-right"
                            iconPos="right"
                            onClick={handleNext}
                            disabled={!isStepValid(activeStep)}
                            className="p-3"
                        />
                    ) : (
                        <Button
                            label="Create Company"
                            icon="pi pi-check"
                            onClick={handleSubmit}
                            loading={submitting}
                            disabled={submitting}
                            className="p-3"
                        />
                    )}
                </div>

                {/* Login Link */}
                <div className="mt-3 text-center">
                    <span className="text-600 font-medium">Already have an account? </span>
                    <Link to="/app/login" className="font-medium no-underline ml-2" style={{ color: 'var(--primary-color)' }}>
                        Sign in here
                    </Link>
                </div>

                {/* Individual Registration Link */}
                <div className="mt-3 pt-3 border-top-1 surface-border text-center">
                    <i className="pi pi-user mr-2 text-600"></i>
                    <span className="text-600 font-medium">Registering as individual? </span>
                    <Link to="/app/register" className="font-medium no-underline ml-2" style={{ color: 'var(--primary-color)' }}>
                        Individual Registration
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default CompanyRegistrationWizard;
